import React from 'react';
import { Term, type AcademySport, type AgeGroup, type Location } from '@court-sync/shared';
import { getSportLabel } from '@/lib/sportPresentation';
import { Button } from '@/components/ui';
import { Input, Field, FieldLabel } from '../ui';
import { 
  Search, 
  Filter,
  X,
  DollarSign
} from 'lucide-react';
import { cn } from '../../utils/cn';

export interface LessonFiltersType {
  searchQuery?: string;
  lessonType?: 'group' | 'private' | 'camp';
  skillLevel?: 'beginner' | 'intermediate' | 'advanced';
  ageGroup?: 'juniors' | 'adults' | 'all';
  dayOfWeek?: number; // 0-6 (Sunday-Saturday)
  termId?: string;
  maxPrice?: number;
  locationId?: string;
}

interface LessonFiltersProps {
  filters: LessonFiltersType;
  onFiltersChange: (filters: LessonFiltersType) => void;
  onReset?: () => void;
  terms: Term[];
  /** When length >= 2, a location dropdown is shown. */
  locations?: Location[];
  className?: string;
  /** When true (e.g. weekly schedule view), day-of-week filter is hidden; parent should clear `dayOfWeek`. */
  hideDayOfWeek?: boolean;
  /** When true, term dropdown is hidden (browse is already scoped to a single focus term). */
  hideTermFilter?: boolean;
  /**
   * Public browse: compact search (not full width), no funnel icon, no type/skill/age/max-price —
   * day / term / location / reset only.
   */
  variant?: 'default' | 'minimal';
  /** Rendered at the start of the minimal filter row (e.g. view mode toggles). */
  startSlot?: React.ReactNode;
  /** Rendered at the end of the minimal filter row (e.g. view mode toggles), left-aligned with filters. */
  endSlot?: React.ReactNode;
  /** Public browse: program dropdown (minimal); parent syncs URL / state via `onProgramChange`. */
  programs?: Array<{ id: string; name: string }>;
  /** Current program from URL (`?program=`) or `'all'`. */
  selectedProgramId?: string | 'all' | null;
  onProgramChange?: (programId: string | 'all') => void;
  /** Public browse: custom age group dropdown (minimal); parent syncs URL via `onAgeGroupChange`. */
  ageGroups?: AgeGroup[];
  /** Current age group from URL (`?ageGroupId=`) or `'all'`. */
  selectedAgeGroupId?: string | 'all' | null;
  onAgeGroupChange?: (ageGroupId: string | 'all') => void;
  /** Public browse: sport dropdown (minimal); parent syncs URL via `onSportChange`. Shown when length >= 2. */
  sports?: AcademySport[];
  /** Current sport from URL (`?sport=`) or `'all'`. */
  selectedSportId?: string | 'all' | null;
  onSportChange?: (academySportId: string | 'all') => void;
  /** Omit "Private" from lesson type filter (catalog does not list private lesson entities). */
  hidePrivateLessonType?: boolean;
}

const DAYS_OF_WEEK = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];

const LESSON_TYPES = [
  { value: 'group', label: 'Group' },
  { value: 'private', label: 'Private' },
  { value: 'camp', label: 'Camp' },
];

const SKILL_LEVELS = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
];

const AGE_GROUPS = [
  { value: 'juniors', label: 'Juniors' },
  { value: 'adults', label: 'Adults' },
  { value: 'all', label: 'All Ages' },
];

export const LessonFilters: React.FC<LessonFiltersProps> = ({
  filters,
  onFiltersChange,
  onReset,
  terms,
  locations = [],
  className,
  hideDayOfWeek = false,
  hideTermFilter = false,
  variant = 'default',
  startSlot,
  endSlot,
  programs = [],
  selectedProgramId = null,
  onProgramChange,
  ageGroups = [],
  selectedAgeGroupId = null,
  onAgeGroupChange,
  sports = [],
  selectedSportId = null,
  onSportChange,
  hidePrivateLessonType = false,
}) => {
  const minimal = variant === 'minimal';
  const lessonTypesForFilter = hidePrivateLessonType
    ? LESSON_TYPES.filter((t) => t.value !== 'private')
    : LESSON_TYPES;
  const updateFilter = <K extends keyof LessonFiltersType>(
    key: K,
    value: LessonFiltersType[K]
  ) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const programFilterActive =
    programs.length > 0 &&
    onProgramChange &&
    selectedProgramId &&
    selectedProgramId !== 'all';

  const ageGroupFilterActive =
    ageGroups.length > 0 &&
    onAgeGroupChange &&
    selectedAgeGroupId &&
    selectedAgeGroupId !== 'all';

  const sportFilterActive =
    sports.length >= 2 &&
    onSportChange &&
    selectedSportId &&
    selectedSportId !== 'all';

  const hasActiveFilters = minimal
    ? Boolean(
        filters.searchQuery ||
          filters.dayOfWeek !== undefined ||
          filters.termId ||
          filters.locationId ||
          programFilterActive ||
          ageGroupFilterActive ||
          sportFilterActive
      )
    : Boolean(
        filters.searchQuery ||
          filters.lessonType ||
          filters.skillLevel ||
          filters.ageGroup ||
          filters.dayOfWeek !== undefined ||
          filters.termId ||
          filters.maxPrice ||
          filters.locationId
      );

  const handleReset = () => {
    if (programFilterActive && onProgramChange) {
      onProgramChange('all');
    }
    if (ageGroupFilterActive && onAgeGroupChange) {
      onAgeGroupChange('all');
    }
    if (sportFilterActive && onSportChange) {
      onSportChange('all');
    }
    if (onReset) {
      onReset();
    } else {
      onFiltersChange({});
    }
  };

  const selectClass =
    'pl-3 pr-10 py-2 border border-border rounded-lg bg-background text-foreground text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring';

  const selectCls = minimal
    ? cn(selectClass, 'h-9 rounded-md py-1 pl-2 pr-8 text-xs')
    : selectClass;

  const minimalControlHeight = minimal ? 'h-9' : 'h-10';

  const searchField = (
    <div className={minimal ? 'min-w-0 w-full flex-1' : 'w-full'}>
      <Field className="gap-2">
        <FieldLabel htmlFor="lesson-search" className="sr-only">Search lessons</FieldLabel>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 z-[1] h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            id="lesson-search"
            type="text"
            value={filters.searchQuery || ''}
            onChange={(e) => updateFilter('searchQuery', e.target.value || undefined)}
            placeholder={minimal ? 'Search…' : 'Search lessons by name or description...'}
            className={cn(
              'pl-8 pr-8 sm:text-sm',
              minimalControlHeight,
              minimal && 'text-xs'
            )}
          />
          {filters.searchQuery && (
            <button
              type="button"
              onClick={() => updateFilter('searchQuery', undefined)}
              className="absolute right-2.5 top-1/2 z-[1] -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </Field>
    </div>
  );

  const daySelect = !hideDayOfWeek ? (
    <select
      value={filters.dayOfWeek !== undefined ? filters.dayOfWeek.toString() : ''}
      onChange={(e) =>
        updateFilter('dayOfWeek', e.target.value ? parseInt(e.target.value) : undefined)
      }
      className={cn(
        selectCls,
        'shrink-0',
        minimalControlHeight,
        minimal && 'max-w-[7.5rem]'
      )}
      aria-label="Filter by day of week"
    >
      <option value="">All Days</option>
      {DAYS_OF_WEEK.map((day, index) => (
        <option key={index} value={index}>
          {day}
        </option>
      ))}
    </select>
  ) : null;

  const termSelect =
    terms.length > 0 && !hideTermFilter ? (
      <select
        value={filters.termId || ''}
        onChange={(e) => updateFilter('termId', e.target.value || undefined)}
        className={cn(
          selectCls,
          'shrink-0',
          minimalControlHeight,
          minimal && 'max-w-[9rem]'
        )}
      >
        <option value="">All Terms</option>
        {terms.map((term) => (
          <option key={term.id} value={term.id}>
            {term.name}
          </option>
        ))}
      </select>
    ) : null;

  const locationSelect =
    locations.length >= 2 ? (
      <select
        value={filters.locationId || ''}
        onChange={(e) => updateFilter('locationId', e.target.value || undefined)}
        className={cn(
          selectCls,
          'shrink-0',
          minimalControlHeight,
          minimal && 'max-w-[9rem]'
        )}
        aria-label="Filter by location"
      >
        <option value="">All locations</option>
        {locations.map((loc) => (
          <option key={loc.id} value={loc.id}>
            {loc.name}
          </option>
        ))}
      </select>
    ) : null;

  const sportSelect =
    minimal && sports.length >= 2 && onSportChange ? (
      <select
        value={selectedSportId && selectedSportId !== 'all' ? selectedSportId : ''}
        onChange={(e) => onSportChange(e.target.value ? e.target.value : 'all')}
        className={cn(selectCls, 'shrink-0', minimalControlHeight, minimal && 'max-w-[9rem]')}
        aria-label="Filter by sport"
      >
        <option value="">All sports</option>
        {sports.map((sport) => (
          <option key={sport.id} value={sport.id}>
            {getSportLabel(sport)}
          </option>
        ))}
      </select>
    ) : null;

  const programSelect =
    minimal && programs.length > 0 && onProgramChange ? (
      <select
        value={
          selectedProgramId && selectedProgramId !== 'all' ? selectedProgramId : ''
        }
        onChange={(e) =>
          onProgramChange(e.target.value ? e.target.value : 'all')
        }
        className={cn(selectCls, 'shrink-0', minimalControlHeight, minimal && 'max-w-[9rem]')}
        aria-label="Filter by program"
      >
        <option value="">All programs</option>
        {programs.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
    ) : null;

  const ageGroupSelect =
    minimal && ageGroups.length > 0 && onAgeGroupChange ? (
      <select
        value={
          selectedAgeGroupId && selectedAgeGroupId !== 'all' ? selectedAgeGroupId : ''
        }
        onChange={(e) =>
          onAgeGroupChange(e.target.value ? e.target.value : 'all')
        }
        className={cn(selectCls, 'shrink-0', minimalControlHeight, minimal && 'max-w-[9rem]')}
        aria-label="Filter by age group"
      >
        <option value="">All age groups</option>
        {ageGroups.map((group) => (
          <option key={group.id} value={group.id}>
            {group.label}
          </option>
        ))}
      </select>
    ) : null;

  const clearButton =
    hasActiveFilters ? (
      <Button
        variant="outline"
        size="sm"
        onClick={handleReset}
        className={cn('shrink-0 text-muted-foreground', minimal && 'h-9 px-2.5 text-xs')}
      >
        <X className={cn('h-4 w-4', !minimal && 'mr-1')} />
        {minimal ? <span className="sr-only">Clear filters</span> : 'Clear Filters'}
      </Button>
    ) : null;

  return (
    <div className={cn(minimal ? 'space-y-2' : 'space-y-4', className)}>
      {minimal ? (
        <div className="rounded-lg border border-border/70 bg-card p-2 shadow-sm">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
            {searchField}
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              {startSlot ? (
                <div className="flex items-center gap-1 border-r border-border pr-1.5 mr-0.5">
                  {startSlot}
                </div>
              ) : null}
              {daySelect}
              {termSelect}
              {sportSelect}
              {programSelect}
              {ageGroupSelect}
              {locationSelect}
              {clearButton}
              {endSlot ? (
                <div className="flex items-center gap-1 border-l border-border pl-1.5 ml-0.5">
                  {endSlot}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <>
          {searchField}

          {/* Filter Row */}
          <div className="flex flex-wrap gap-3 items-center">
            <Filter className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />

            {/* Lesson Type */}
            <select
              value={filters.lessonType || ''}
              onChange={(e) =>
                updateFilter(
                  'lessonType',
                  e.target.value as LessonFiltersType['lessonType'] || undefined
                )
              }
              className={selectClass}
            >
              <option value="">All Types</option>
              {lessonTypesForFilter.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>

            {/* Skill Level */}
            <select
              value={filters.skillLevel || ''}
              onChange={(e) =>
                updateFilter(
                  'skillLevel',
                  e.target.value as LessonFiltersType['skillLevel'] || undefined
                )
              }
              className={selectClass}
            >
              <option value="">All Skill Levels</option>
              {SKILL_LEVELS.map((level) => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>

            {/* Age Group */}
            <select
              value={filters.ageGroup || ''}
              onChange={(e) =>
                updateFilter(
                  'ageGroup',
                  e.target.value as LessonFiltersType['ageGroup'] || undefined
                )
              }
              className={selectClass}
            >
              <option value="">All Age Groups</option>
              {AGE_GROUPS.map((group) => (
                <option key={group.value} value={group.value}>
                  {group.label}
                </option>
              ))}
            </select>

            {daySelect}
            {termSelect}
            {locationSelect}

            {/* Max Price */}
            <Field className="w-32 max-w-full shrink-0 gap-2">
              <FieldLabel htmlFor="lesson-max-price" className="sr-only">Max price</FieldLabel>
              <div className="relative">
                <DollarSign
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  id="lesson-max-price"
                  type="number"
                  value={filters.maxPrice || ''}
                  onChange={(e) =>
                    updateFilter('maxPrice', e.target.value ? parseFloat(e.target.value) : undefined)
                  }
                  placeholder="Max Price"
                  min={0}
                  className="h-10 w-full min-w-0 pl-9 sm:text-sm"
                />
              </div>
            </Field>

            {clearButton}
          </div>
        </>
      )}

      {/* Active Filters Pills */}
      {hasActiveFilters && !minimal && (
        <div className="flex flex-wrap gap-2">
          {filters.lessonType && !minimal && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm">
              Type: {LESSON_TYPES.find(t => t.value === filters.lessonType)?.label}
              <button onClick={() => updateFilter('lessonType', undefined)}>
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          )}
          {filters.skillLevel && !minimal && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-sm">
              Skill: {SKILL_LEVELS.find(l => l.value === filters.skillLevel)?.label}
              <button onClick={() => updateFilter('skillLevel', undefined)}>
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          )}
          {filters.ageGroup && !minimal && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-full text-sm">
              Age: {AGE_GROUPS.find(g => g.value === filters.ageGroup)?.label}
              <button onClick={() => updateFilter('ageGroup', undefined)}>
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          )}
          {filters.dayOfWeek !== undefined && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded-full text-sm">
              Day: {DAYS_OF_WEEK[filters.dayOfWeek]}
              <button onClick={() => updateFilter('dayOfWeek', undefined)}>
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          )}
          {filters.termId && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 rounded-full text-sm">
              Term: {terms.find(t => t.id === filters.termId)?.name}
              <button onClick={() => updateFilter('termId', undefined)}>
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          )}
          {filters.locationId && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-100 dark:bg-slate-900/30 text-slate-800 dark:text-slate-300 rounded-full text-sm">
              Location: {locations.find((l) => l.id === filters.locationId)?.name ?? filters.locationId}
              <button onClick={() => updateFilter('locationId', undefined)}>
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          )}
          {filters.maxPrice && !minimal && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-300 rounded-full text-sm">
              Max: ${filters.maxPrice}
              <button onClick={() => updateFilter('maxPrice', undefined)}>
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          )}
          {minimal && programFilterActive && onProgramChange ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-teal-100 px-2.5 py-1 text-sm text-teal-800 dark:bg-teal-900/30 dark:text-teal-300">
              Program: {programs.find((p) => p.id === selectedProgramId)?.name ?? selectedProgramId}
              <button type="button" onClick={() => onProgramChange('all')}>
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ) : null}
          {minimal && sportFilterActive && onSportChange ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-sm text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
              Sport:{' '}
              {(() => {
                const sport = sports.find((s) => s.id === selectedSportId);
                return sport ? getSportLabel(sport) : selectedSportId;
              })()}
              <button type="button" onClick={() => onSportChange('all')}>
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default LessonFilters;
